import { A } from '@ember/array';
import { service } from '@ember/service';
import type StoreService from '@ember-data/store';
import Component from '@glimmer/component';
import { dropTask } from 'ember-concurrency';
import PowerSelect from 'ember-power-select/components/power-select';
import type Group from 'garaje/models/group';
import type SkinnyLocation from 'garaje/models/skinny-location';
import type MetricsService from 'garaje/services/metrics';

interface LocationsTableGroupSelectionSignature {
  Args: {
    /**
     * List of available groups to be assigned
     */
    groups: Group[];
    location: SkinnyLocation;
  };
  Element: HTMLDivElement;
}

/**
 * List of locations with group assignment capabilities
 */
export default class LocationsTableGroupSelection extends Component<LocationsTableGroupSelectionSignature> {
  @service declare store: StoreService;
  @service declare metrics: MetricsService;

  toggleGroupTask = dropTask(async (newGroup: Group) => {
    // Clean existing one
    const existingGroup = this.args.location.group;
    if (existingGroup) {
      A(existingGroup.locations.content).removeObject(this.args.location);
      await existingGroup.save();
      this.metrics.trackEvent('Group Action Taken', {
        action: 'group_members_removed',
        target_group: existingGroup.name,
        target_group_members: [this.args.location.name],
      });
    }
    // Add new group if passed
    if (newGroup) {
      A(newGroup.locations.content).pushObject(this.args.location);
      await newGroup.save();
      this.metrics.trackEvent('Group Action Taken', {
        action: 'group_members_added',
        target_group: newGroup.name,
        target_group_members: [this.args.location.name],
      });
    }
    // If the real location is already loaded, reload it.
    const realLocation = this.store.peekRecord('location', this.args.location.id);
    if (realLocation) {
      await realLocation.reload();
    }
  });

  <template>
    <div ...attributes>
      <PowerSelect
        @disabled={{this.toggleGroupTask.isRunning}}
        @allowClear={{true}}
        @options={{@groups}}
        @onChange={{this.toggleGroupTask.perform}}
        @selected={{@location.group}}
        as |group|
      >
        {{group.name}}
      </PowerSelect>
    </div>
  </template>
}
